'use client';

import { motion } from 'framer-motion';
import { ArrowRight, Atom, Rocket, TrendingUp } from 'lucide-react';

import { themes, useTheme } from '@/contexts/ThemeContext';

export default function HeroSection() {
  const { currentTheme } = useTheme();

  return (
    <div
      id="hero"
      className="relative min-h-screen w-full transition-colors duration-700"
      style={{
        background: themes[currentTheme].bgGradient,
        color: themes[currentTheme].text,
      }}
    >
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Decorative circles */}
        <motion.div
          className="absolute -left-4 top-1/4 size-6 rounded-full sm:size-8"
          style={{ background: themes[currentTheme].gradient }}
          animate={{
            y: [0, 10, 0],
            rotate: [0, 360],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute right-1/4 top-1/3 size-4 rounded-full sm:size-6"
          style={{ background: themes[currentTheme].gradient }}
          animate={{
            x: [0, 15, 0],
            rotate: [0, -360],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Floating icons */}
        <motion.div
          className="absolute left-1/4 top-1/4 hidden sm:block"
          animate={{
            y: [0, -20, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <TrendingUp
            className="size-6 opacity-50 sm:size-8"
            style={{ color: themes[currentTheme].text }}
          />
        </motion.div>
        <motion.div
          className="absolute right-1/3 top-1/2 hidden sm:block"
          animate={{
            y: [0, 20, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <Rocket
            className="size-8 opacity-50 sm:size-10"
            style={{ color: themes[currentTheme].text }}
          />
        </motion.div>
      </div>

      {/* Main content */}
      <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col gap-16 px-4 py-16 pt-24 sm:px-6 sm:py-24 md:gap-24 lg:px-8">
        <div className="relative mx-auto mt-8 max-w-3xl text-center sm:mt-10">
          {/* Floating Team Images */}
          <motion.div
            className="absolute -left-4 top-4 sm:-left-20 sm:top-0"
            animate={{
              y: [0, -10, 0],
              rotate: [-5, 5, -5],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <div className="size-16 overflow-hidden rounded-2xl bg-purple-400/30 p-1 sm:size-20 sm:bg-purple-400" />
          </motion.div>

          <motion.div
            className="absolute -right-4 top-8 sm:-right-16 sm:top-0"
            animate={{
              y: [0, -15, 0],
              rotate: [5, -5, 5],
            }}
            transition={{
              duration: 7.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <div className="size-14 overflow-hidden rounded-2xl bg-blue-400/30 p-1 sm:size-20 sm:bg-blue-400" />
          </motion.div>

          <motion.div
            className="absolute -left-4 bottom-8 sm:-left-16 sm:bottom-0"
            animate={{
              y: [0, 12, 0],
              rotate: [-8, 8, -8],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <div className="size-14 overflow-hidden rounded-2xl bg-green-400/30 p-1 sm:size-20 sm:bg-green-400" />
          </motion.div>

          <motion.div
            className="absolute -right-4 bottom-4 sm:-right-16 sm:bottom-4"
            animate={{
              y: [0, 10, 0],
              rotate: [5, -5, 5],
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <div className="size-20 overflow-hidden rounded-2xl bg-orange-400/30 p-1 sm:size-24 sm:bg-orange-400" />
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-6 sm:space-y-8"
          >
            <motion.div
              className="relative"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <h1
                className="font-space-grotesk text-5xl font-bold tracking-tight sm:text-7xl"
              >
                <span className="block">We&apos;re the OGs of</span>
                <span
                  className={`text-gradient-${currentTheme} block text-7xl font-extrabold sm:text-8xl`}
                >
                  Early-Stage Investing!
                </span>
              </h1>

              {/* Decorative elements */}
              <motion.div
                className="absolute -right-4 top-0 hidden sm:-right-10 sm:block"
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              >
                <Atom className="size-6 text-white sm:size-8" />
              </motion.div>
            </motion.div>

            <motion.p
              className="mx-auto max-w-2xl px-6 text-xl leading-8 sm:px-0 sm:text-lg sm:leading-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              style={{ color: themes[currentTheme].text }}
            >
              Offering collaborative capital, we&apos;re the trusted ally for
              founders at stages when others hesitate.
            </motion.p>

            <motion.div
              className="flex justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <motion.a
                href="#portfolio"
                className="group relative inline-flex items-center gap-2 rounded-full border-2 px-6 py-2.5 text-sm font-semibold shadow-md transition-all hover:shadow-lg sm:px-8 sm:py-3 sm:text-base"
                style={{
                  borderColor: themes[currentTheme].primary,
                  color: themes[currentTheme].text,
                }}
                whileHover={{
                  scale: 1.05,
                  boxShadow: `0 0 30px ${themes[currentTheme].primary}60`,
                }}
                whileTap={{ scale: 0.95 }}
                onClick={(e) => {
                  e.preventDefault();
                  const section = document.getElementById('portfolio');
                  if (section) {
                    section.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <span>Explore Portfolio</span>
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1 sm:size-5" />
              </motion.a>
              <motion.a
                href="#contact"
                className="group relative inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg sm:px-8 sm:py-3 sm:text-base"
                style={{
                  background: themes[currentTheme].gradient,
                }}
                whileHover={{
                  scale: 1.05,
                  boxShadow: `0 0 30px ${themes[currentTheme].primary}60`,
                }}
                whileTap={{ scale: 0.95 }}
                onClick={(e) => {
                  e.preventDefault();
                  const section = document.getElementById('contact');
                  if (section) {
                    section.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <span>Get in Touch</span>
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1 sm:size-5" />
              </motion.a>
            </motion.div>
          </motion.div>
        </div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mx-auto w-full max-w-7xl"
        >
          <dl className="grid grid-cols-3 gap-2 text-center sm:grid-cols-1 sm:gap-8 lg:grid-cols-3">
            {[
              {
                title: '43% IRR',
                description: 'Consistent returns for a decade',
              },
              {
                title: '300+',
                description: 'Companies in our portfolio',
              },
              {
                title: '50%',
                description: 'Unrealized IRR',
              },
            ].map((stat) => (
              <motion.div
                key={stat.title}
                className="rounded-xl bg-black/10 px-2 py-3 backdrop-blur-lg sm:rounded-3xl sm:p-8"
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <dt
                  className={`font-space-grotesk text-gradient-${currentTheme} mb-0.5 text-lg font-bold sm:mb-2 sm:text-4xl`}
                >
                  {stat.title}
                </dt>
                <dd
                  className="text-[10px] leading-tight sm:text-sm"
                  style={{ color: themes[currentTheme].text }}
                >
                  {stat.description}
                </dd>
              </motion.div>
            ))}
          </dl>
        </motion.div>
      </div>
    </div>
  );
}
